<template>
  <avs-card>
    <template #title>
      <v-toolbar flat class="mb-4 bgTable">
        <!-- Titre -->
        <v-icon left color="light-green darken-2" class="mr-2">mdi-auto-fix</v-icon>
        <span class="font-weight-bold mr-6">Corrections automatiques OCR</span>
        <v-chip small color="light-green lighten-4" class="light-green--text text--darken-2 mr-4">
          {{ items.length }} transaction{{ items.length > 1 ? 's' : '' }}
        </v-chip>
        <!-- Recherche -->
        <v-text-field
          class="mr-4"
          dense
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          label="Rechercher"
          v-model="search"
          @input="debouncedSync"
          style="max-width:260px"
        />
        <!-- Date début -->
        <v-menu v-model="startMenu" transition="scale-transition" offset-y>
          <template #activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs" v-on="on"
              dense hide-details readonly class="mr-4"
              label="Date début"
              :value="startDate"
              clearable
              @click:clear="startDate = null; fetchReset()"
              style="max-width:150px"
            />
          </template>
          <v-date-picker v-model="startDate" @input="startMenu = false; fetchReset()" scrollable />
        </v-menu>
        <!-- Date fin -->
        <v-menu v-model="endMenu" transition="scale-transition" offset-y>
          <template #activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs" v-on="on"
              dense hide-details readonly class="mr-4"
              label="Date fin"
              :value="endDate"
              clearable
              @click:clear="endDate = null; fetchReset()"
              style="max-width:150px"
            />
          </template>
          <v-date-picker v-model="endDate" @input="endMenu = false; fetchReset()" scrollable />
        </v-menu>
        <v-spacer />
        <v-btn icon @click="fetchReset()" :loading="loading" title="Rafraîchir">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>
    </template>

    <template #content>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading && page === 1"
        :items-per-page="-1"
        dense
        hide-default-footer
        class="elevation-0 ma-6 bgTable"
      >
        <!-- Date -->
        <template #item.created_at="{ item }">
          {{ toDatetime(item.created_at) }}
        </template>

        <!-- Utilisateur / Société -->
        <template #item.user="{ item }">
          <div>
            <div class="font-weight-medium">{{ getUserOrDriverName(item) }}</div>
            <div class="caption grey--text">
              {{ getUserOrVehicleContact(item) }} • {{ getComanyName(item) }}
            </div>
          </div>
        </template>

        <!-- Ancien produit → Nouveau produit -->
        <template #item.product_correction="{ item }">
          <div v-if="item.fraud_details && item.fraud_details.product_correction" class="d-flex align-center">
            <v-chip x-small label color="grey lighten-2" class="grey--text text--darken-2">
              {{ item.fraud_details.product_correction.old_product_name || '?' }}
            </v-chip>
            <v-icon x-small class="mx-1" color="light-green darken-2">mdi-arrow-right</v-icon>
            <v-chip x-small label color="light-green lighten-4" class="light-green--text text--darken-2 font-weight-bold">
              {{ item.fraud_details.product_correction.new_product_name || item.product }}
            </v-chip>
          </div>
          <span v-else>{{ item.product || '-' }}</span>
        </template>

        <!-- Ancien montant → Nouveau montant -->
        <template #item.amount_correction="{ item }">
          <div v-if="item.fraud_details && item.fraud_details.product_correction && item.fraud_details.original_transaction_data">
            <span class="grey--text text-decoration-line-through">
              {{ formatAmount(item.fraud_details.original_transaction_data.amount) }} XOF
            </span>
            <v-icon x-small class="mx-1" color="light-green darken-2">mdi-arrow-right</v-icon>
            <span class="font-weight-bold light-green--text text--darken-2">
              {{ formatAmount(item.amount) }} XOF
            </span>
          </div>
          <span v-else>{{ formatAmount(item.amount) }} XOF</span>
        </template>

        <!-- Prix unitaire détecté -->
        <template #item.resolved_unit_price="{ item }">
          <span v-if="item.fraud_details && item.fraud_details.product_correction && item.fraud_details.product_correction.resolved_unit_price">
            {{ item.fraud_details.product_correction.resolved_unit_price }} XOF/L
          </span>
          <span v-else class="grey--text">-</span>
        </template>

        <!-- Quantité -->
        <template #item.quantity="{ item }">
          {{ item.quantity }} L
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <v-btn
            icon small
            :disabled="!item.image && !item.vehicle_image"
            @click="openAttachments(item)"
            title="Voir les pièces jointes"
          >
            <v-icon small>mdi-paperclip</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <!-- Loader scroll infini -->
      <div class="py-4 d-flex justify-center" v-if="loadingMore">
        <v-progress-circular indeterminate />
      </div>

      <!-- Fin de liste -->
      <div v-if="!loading && !loadingMore && !hasMore && items.length" class="text-center grey--text py-4">
        Fin de la liste
      </div>
      <div v-if="!loading && !items.length" class="text-center grey--text py-8">
        <v-icon large color="grey lighten-1">mdi-check-circle-outline</v-icon>
        <div class="mt-2">Aucune transaction corrigée automatiquement</div>
      </div>

      <div ref="sentinel" style="height:1px;"></div>

      <!-- Dialog pièces jointes -->
      <v-dialog v-model="attachDialog" max-width="700" scrollable>
        <v-card v-if="selectedItem">
          <v-card-title class="text-h6">
            <v-icon left color="light-green darken-2">mdi-auto-fix</v-icon>
            Correction OCR · #{{ selectedItem.transaction_number }}
            <v-spacer />
            <v-btn icon @click="attachDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <!-- Résumé correction -->
            <v-alert dense text color="light-green" icon="mdi-swap-horizontal" class="mb-4" v-if="selectedItem.fraud_details && selectedItem.fraud_details.product_correction">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="caption grey--text">Produit</div>
                  <div>
                    <span class="grey--text">{{ selectedItem.fraud_details.product_correction.old_product_name }}</span>
                    <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                    <strong>{{ selectedItem.fraud_details.product_correction.new_product_name }}</strong>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" v-if="selectedItem.fraud_details.original_transaction_data">
                  <div class="caption grey--text">Montant</div>
                  <div>
                    <span class="grey--text">{{ selectedItem.fraud_details.original_transaction_data.amount }} XOF</span>
                    <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                    <strong>{{ selectedItem.amount }} XOF</strong>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="caption grey--text mt-1">
                    Prix unitaire OCR : <strong>{{ selectedItem.fraud_details.product_correction.resolved_unit_price }} XOF/L</strong>
                    · Quantité : <strong>{{ selectedItem.quantity }} L</strong>
                  </div>
                </v-col>
              </v-row>
            </v-alert>

            <!-- Image pompe -->
            <div v-if="selectedItem.image" class="mb-3">
              <div class="subtitle-2 mb-1">Photo pompe</div>
              <v-img :src="selectedItem.image" max-height="400" contain class="rounded" />
              <v-btn x-small text color="primary" class="mt-1" :href="selectedItem.image" target="_blank">
                <v-icon x-small left>mdi-open-in-new</v-icon>Ouvrir en grand
              </v-btn>
            </div>
            <!-- Image véhicule -->
            <div v-if="selectedItem.vehicle_image">
              <div class="subtitle-2 mb-1">Photo véhicule</div>
              <v-img :src="selectedItem.vehicle_image" max-height="400" contain class="rounded" />
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="attachDialog = false">Fermer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import { toDatetime } from "@/utils/datetime"
import debounce from "lodash/debounce"

export default {
  name: "AdminOcrCorrected",

  data() {
    return {
      items: [],
      loading: false,
      loadingMore: false,
      hasMore: true,
      page: 1,
      search: "",
      startDate: null,
      endDate: null,
      startMenu: false,
      endMenu: false,
      observer: null,
      attachDialog: false,
      selectedItem: null,
      headers: [
        { text: "Date", value: "created_at", sortable: false, width: 160 },
        { text: "Bénéficiaire", value: "user", sortable: false },
        { text: "Produit corrigé", value: "product_correction", sortable: false },
        { text: "Montant corrigé", value: "amount_correction", sortable: false },
        { text: "Prix OCR détecté", value: "resolved_unit_price", sortable: false },
        { text: "Qté", value: "quantity", sortable: false, width: 80 },
        { text: "", value: "actions", sortable: false, align: "center", width: 60 },
      ],
    }
  },

  mounted() {
    this.fetchReset()
    this.$nextTick(() => this.createObserver())
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },

  methods: {
    toDatetime,

    debouncedSync: debounce(function () {
      this.fetchReset()
    }, 400),

    params() {
      const p = { page: this.page, ordering: "-created_at", ocr_corrected: "true" }
      if (this.search) p.search = this.search
      if (this.startDate) p.startDate = this.startDate
      if (this.endDate) p.endDate = this.endDate
      return p
    },

    async fetch({ reset = false } = {}) {
      if (reset) this.loading = true
      else this.loadingMore = true
      try {
        const data = await AdminServices.getActivities(this, this.params())
        let results = []
        if (Array.isArray(data)) results = data
        else if (Array.isArray(data?.results)) results = data.results
        else if (Array.isArray(data?.results?.results)) results = data.results.results
        this.items = reset ? results : this.items.concat(results)
        this.hasMore = !!(data && data.next)
      } catch (e) {
        if (e?.response?.status === 404) this.hasMore = false
        else console.error("Erreur chargement corrections OCR:", e)
      } finally {
        this.loading = false
        this.loadingMore = false
        this.$nextTick(() => {
          if (this.hasMore && this.observer && this.$refs.sentinel) {
            this.observer.unobserve(this.$refs.sentinel)
            this.observer.observe(this.$refs.sentinel)
          }
        })
      }
    },

    fetchReset() {
      this.page = 1
      this.hasMore = true
      this.items = []
      this.fetch({ reset: true })
    },

    createObserver() {
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) this.loadMore() },
        { root: null, rootMargin: "0px", threshold: 0.1 }
      )
      this.observer.observe(el)
    },

    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page += 1
      this.fetch()
    },

    openAttachments(item) {
      this.selectedItem = item
      this.attachDialog = true
    },

    formatAmount(val) {
      if (val === null || val === undefined) return "-"
      return Number(val).toLocaleString("fr-FR")
    },

    getUserOrDriverName(item) {
      if (item.user?.phone_number === "+2250000000009") {
        const v = item.user?.vehicles?.[0]
        if (v) return `${v.driver_first_name || ""} ${v.driver_last_name || ""}`.trim() || "Chauffeur"
      }
      return `${item.user?.first_name || ""} ${item.user?.last_name || ""}`.trim() || item.user?.phone_number || "-"
    },

    getUserOrVehicleContact(item) {
      if (item.user?.phone_number === "+2250000000009") {
        return item.user?.vehicles?.[0]?.registration_number || ""
      }
      return item.user?.phone_number || ""
    },

    getComanyName(item) {
      if (item.user?.phone_number === "+2250000000009") {
        return item.created_by?.company?.name || ""
      }
      return ""
    },
  },
}
</script>
